/** Stock-card coefficients are alternative units per primary unit, never inferred from names. */
export function normalizeProductionUnit(value: string): string {
  const unit = value.trim().toLocaleUpperCase("tr-TR");
  if (unit === "KOLI") return "KOLİ";
  if (unit === "SILINDIR") return "SİLİNDİR";
  return unit;
}
export type QuantityEvidence = {
  type: string; quantity: number; unit: string; voidedAt: string | null;
  canonicalQuantity?: number | null; primaryUnit?: string; unitFactorToPrimary?: number | null;
};
export function canonicalEntryQuantity(entry: QuantityEvidence, primaryUnit: string): number | null {
  if (entry.voidedAt) return 0;
  const primary = normalizeProductionUnit(primaryUnit);
  // Only absent pre-migration fields can be interpreted as a same-unit legacy entry.
  // An explicitly null conversion is an unresolved fact, not permission to guess.
  if (entry.canonicalQuantity === undefined && normalizeProductionUnit(entry.unit) === primary) {
    return Number.isFinite(entry.quantity) && entry.quantity >= 0 ? entry.quantity : null;
  }
  if (entry.canonicalQuantity == null || !Number.isFinite(entry.canonicalQuantity) || entry.canonicalQuantity < 0 ||
      !entry.primaryUnit || normalizeProductionUnit(entry.primaryUnit) !== primary) return null;
  return entry.canonicalQuantity;
}
export function quantityTotals(entries: readonly QuantityEvidence[], primaryUnit: string) {
  let production = 0, waste = 0, scrap = 0;
  let unknownProduction = 0, unknownWaste = 0, unknownScrap = 0;
  for (const entry of entries) {
    if (entry.voidedAt) continue;
    const quantity = canonicalEntryQuantity(entry, primaryUnit);
    if (entry.type === "PRODUCTION") { if (quantity === null) unknownProduction++; else { const sum = sumKnownQuantities([production, quantity]); if (sum === null) unknownProduction++; else production = sum; } }
    else if (entry.type === "WASTE") { if (quantity === null) unknownWaste++; else { const sum = sumKnownQuantities([waste, quantity]); if (sum === null) unknownWaste++; else waste = sum; } }
    else { if (quantity === null) unknownScrap++; else { const sum = sumKnownQuantities([scrap, quantity]); if (sum === null) unknownScrap++; else scrap = sum; } }
  }
  return {
    production: unknownProduction ? null : production,
    waste: unknownWaste ? null : waste,
    scrap: unknownScrap ? null : scrap,
    knownProduction: production, knownWaste: waste, knownScrap: scrap,
    unknownProduction, unknownWaste, unknownScrap,
    unknownCount: unknownProduction + unknownWaste + unknownScrap,
  };
}
export function convertQuantityToPrimary(quantity: number, factor: number): number | null {
  if (!Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(factor) || factor <= 0) return null;
  const result = Number((quantity / factor).toFixed(12));
  return Number.isFinite(result) && result > 0 ? result : null;
}
/** Validate before the database can silently round the original entered amount. */
export function validQuantityPrecision(value: number): boolean {
  return Number.isFinite(value) && value > 0 && value <= 1_000_000_000 &&
    Number(value.toFixed(12)) === value &&
    Number(value.toPrecision(15)) === value;
}

export type QuantityGroup = {
  unit: string; production: number | null; waste: number | null; scrap: number | null; unknownCount: number;
};
export function sumKnownQuantities(values: readonly (number | null)[]): number | null {
  if (values.some((value) => value === null || !Number.isFinite(value))) return null;
  let total = 0;
  for (const value of values) { if (value !== null) total = Number((total + value).toFixed(12)); }
  return Number.isFinite(total) && Math.abs(total) <= Number.MAX_SAFE_INTEGER ? total : null;
}
export function aggregateQuantityGroups(runs: readonly { unit: string; entries: readonly QuantityEvidence[] }[]): QuantityGroup[] {
  const groups = new Map<string, QuantityGroup>();
  for (const run of runs) {
    const unit = normalizeProductionUnit(run.unit);
    const totals = quantityTotals(run.entries, unit);
    const previous = groups.get(unit) ?? { unit, production: 0, waste: 0, scrap: 0, unknownCount: 0 };
    groups.set(unit, {
      unit,
      production: sumKnownQuantities([previous.production, totals.production]),
      waste: sumKnownQuantities([previous.waste, totals.waste]),
      scrap: sumKnownQuantities([previous.scrap, totals.scrap]),
      unknownCount: previous.unknownCount + totals.unknownCount,
    });
  }
  return [...groups.values()].sort((a, b) => a.unit.localeCompare(b.unit, "tr-TR"));
}
export function singleQuantityGroup(groups: readonly QuantityGroup[]) {
  return groups.length === 1 ? groups[0] : { unit: null, production: null, waste: null, scrap: null, unknownCount: groups.reduce((sum, group) => sum + group.unknownCount, 0) };
}
